import React, { useState } from 'react';
import {
  buildStudioWhatsAppUrl,
  clientWasEmailed,
  replyWhatsAppText,
  whyClientWasNotEmailed,
} from '../../utils/studioNotifyChannels';
import './ReplyReach.css';

export interface SentReply {
  text: string;
  delivery?: string;
  destination?: string;
  providerAccepted?: boolean;
}

interface ReplyReachProps {
  sent: SentReply;
  clientName?: string;
  phoneE164?: string;
  clientLink?: string;
  /** Once the booking is confirmed, the link is labelled as the booking. */
  booking?: boolean;
  /** Saves a studio-only note so the conversation shows how the client was told. */
  onRecord: (text: string) => Promise<void> | void;
  onClose: () => void;
}

/**
 * Shown after every studio message. When the email did not go out it stays until the client has
 * been told some other way, so nobody is left not knowing.
 */
const ReplyReach: React.FC<ReplyReachProps> = ({ sent, clientName, phoneE164, clientLink, booking, onRecord, onClose }) => {
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const emailed = clientWasEmailed(sent.delivery, sent.providerAccepted);
  const whatsAppUrl = buildStudioWhatsAppUrl(phoneE164, replyWhatsAppText({ clientName, text: sent.text, link: clientLink, booking }));
  const who = String(clientName || '').trim().split(/\s+/)[0] || 'The client';
  const quoted = sent.text.trim().slice(0, 1800);

  const openWhatsApp = () => {
    window.open(whatsAppUrl, '_blank', 'noopener,noreferrer');
    setOpened(true);
  };

  const record = async (text: string) => {
    setBusy(true);
    try {
      await onRecord(text);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  if (emailed) {
    return (
      <div className="reply-reach is-done" role="status" data-testid="reply-reach">
        <p>Emailed to {sent.destination}. {who} can also read it on their page.</p>
        <div className="reply-reach-actions">
          {whatsAppUrl ? (
            <button type="button" className="studio-text-action" onClick={openWhatsApp}>Also send on WhatsApp</button>
          ) : null}
          <button type="button" className="studio-text-action" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="reply-reach is-urgent" role="alert" data-testid="reply-reach">
      <strong>{who} has not been told yet.</strong>
      <p>{whyClientWasNotEmailed(sent.delivery, sent.destination)} The message is on their page, but they will not know to look.</p>
      {opened ? (
        <>
          <p>Did you send it in WhatsApp?</p>
          <div className="reply-reach-actions">
            <button type="button" className="studio-primary" disabled={busy} onClick={() => record(`Sent on WhatsApp: ${quoted}`)}>
              Yes, it is sent
            </button>
            <button type="button" className="studio-secondary" disabled={busy} onClick={openWhatsApp}>Open WhatsApp again</button>
          </div>
        </>
      ) : (
        <div className="reply-reach-actions">
          {whatsAppUrl ? (
            <button type="button" className="studio-primary" onClick={openWhatsApp} data-testid="reply-reach-whatsapp">
              Send on WhatsApp
            </button>
          ) : (
            <p>There is no mobile number either. Phone them, or add an email or mobile number to their details.</p>
          )}
          <button
            type="button"
            className="studio-secondary"
            disabled={busy}
            onClick={() => record(`Told ${who} another way about: ${quoted}`)}
          >
            I told them another way
          </button>
        </div>
      )}
    </div>
  );
};

export default ReplyReach;
